import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { ArrowUpDown, Plus, Search, X } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../../components/ui/popover';
import { SortChip } from './SortChip';
import type {
  SortDefinition,
  TableExplorerColumn,
} from '../../core/types/workspaceTypes';
import { normalizeColumnName } from '../../common/utils/normalizeColumnName';

type SortWizardProps = {
  sorting: SortDefinition[];
  columns: TableExplorerColumn[];
  onSortingChange: (sorting: SortDefinition[]) => void;
  // Optional: column to add as a sort (from toolbar)
  initialDraftColumn?: string | null;
  // Called when the initial draft has been handled
  onDraftHandled?: () => void;
};

export function SortWizard({
  sorting,
  columns,
  onSortingChange,
  initialDraftColumn,
  onDraftHandled,
}: SortWizardProps) {
  const [isAddingSort, setIsAddingSort] = useState(false);
  const [columnSearch, setColumnSearch] = useState('');
  const [selectedColumnIndex, setSelectedColumnIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  // Track if we've already processed the initial draft column
  const processedInitialColumnRef = useRef<string | null>(null);

  const normalizedSortedFields = useMemo(() => {
    return new Set(sorting.map((s) => normalizeColumnName(s.field)));
  }, [sorting]);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // Require 5px movement before starting drag
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Generate stable IDs for sortable items
  const sortIds = useMemo(
    () => sorting.map((s) => `sort-${s.field}`),
    [sorting]
  );

  // Filter columns by search
  const filteredColumns = useMemo(() => {
    if (!columnSearch.trim()) return columns;
    const search = columnSearch.toLowerCase();
    return columns.filter((col) => col.name.toLowerCase().includes(search));
  }, [columns, columnSearch]);

  // Get selectable columns (exclude ones that already have sorts)
  const selectableColumns = useMemo(() => {
    return filteredColumns.filter((col) => {
      return !normalizedSortedFields.has(normalizeColumnName(col.name));
    });
  }, [filteredColumns, normalizedSortedFields]);

  // Reset selected index when search changes or popover opens
  useEffect(() => {
    setSelectedColumnIndex(0);
  }, [columnSearch, isAddingSort]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current && selectedColumnIndex >= 0) {
      const selectedButton = listRef.current.querySelector(`[data-index="${selectedColumnIndex}"]`);
      selectedButton?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedColumnIndex]);

  // Handle initialDraftColumn - add the sort directly (no draft mode needed for sorts)
  useEffect(() => {
    const normalizedInitialDraftColumn = initialDraftColumn
      ? normalizeColumnName(initialDraftColumn)
      : null;

    if (normalizedInitialDraftColumn &&
      normalizedInitialDraftColumn !== processedInitialColumnRef.current) {
      const column = columns.find(
        (c) => normalizeColumnName(c.name) === normalizedInitialDraftColumn
      );
      if (column && !normalizedSortedFields.has(normalizeColumnName(column.name))) {
        const newSort: SortDefinition = {
          field: column.name,
          direction: 'asc',
        };
        onSortingChange([...sorting, newSort]);
        processedInitialColumnRef.current = normalizedInitialDraftColumn;
        onDraftHandled?.();
      } else {
        // Column not found or already sorted, just clear
        processedInitialColumnRef.current = normalizedInitialDraftColumn;
        onDraftHandled?.();
      }
    }
  }, [initialDraftColumn, columns, sorting, onSortingChange, onDraftHandled, normalizedSortedFields]);

  // Handle drag end
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = sortIds.indexOf(active.id as string);
        const newIndex = sortIds.indexOf(over.id as string);

        if (oldIndex !== -1 && newIndex !== -1) {
          const newSorting = arrayMove(sorting, oldIndex, newIndex);
          onSortingChange(newSorting);
        }
      }
    },
    [sortIds, sorting, onSortingChange]
  );

  // Add a new sort
  const handleAddSort = useCallback(
    (column: TableExplorerColumn) => {
      const newSort: SortDefinition = {
        field: column.name,
        direction: 'asc', // Default to ascending
      };
      onSortingChange([...sorting, newSort]);
      setIsAddingSort(false);
      setColumnSearch('');
    },
    [sorting, onSortingChange]
  );

  // Toggle sort direction
  const handleToggleDirection = useCallback(
    (index: number) => {
      const newSorting = [...sorting];
      newSorting[index] = {
        ...newSorting[index],
        direction: newSorting[index].direction === 'asc' ? 'desc' : 'asc',
      };
      onSortingChange(newSorting);
    },
    [sorting, onSortingChange]
  );

  // Remove a sort
  const handleRemoveSort = useCallback(
    (index: number) => {
      const newSorting = sorting.filter((_, i) => i !== index);
      onSortingChange(newSorting);
    },
    [sorting, onSortingChange]
  );

  // Clear all sorts
  const handleClearAll = useCallback(() => {
    onSortingChange([]);
  }, [onSortingChange]);

  // Check if a column already has a sort
  const columnHasSort = useCallback(
    (columnName: string) => {
      const normalized = normalizeColumnName(columnName);
      return sorting.some((s) => normalizeColumnName(s.field) === normalized);
    },
    [sorting]
  );

  // Handle keyboard navigation in the column list
  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (selectableColumns.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedColumnIndex((prev) =>
            prev < selectableColumns.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedColumnIndex((prev) => (prev > 0 ? prev - 1 : 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedColumnIndex >= 0 && selectedColumnIndex < selectableColumns.length) {
            handleAddSort(selectableColumns[selectedColumnIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setIsAddingSort(false);
          break;
      }
    },
    [selectableColumns, selectedColumnIndex, handleAddSort]
  );

  // Don't render if no columns available
  if (columns.length === 0) {
    return null;
  }

  // PP-06 — the whole bar (label, chips, "then by...", Clear All) lives as
  // flex-wrap siblings of ONE row so it collapses to a single 34-36px line
  // and only wraps when the content genuinely doesn't fit — no more fixed
  // two-row layout (label row + separate chips row) that reserved a second
  // line even for a single sort. DndContext/SortableContext render no DOM
  // of their own, so wrapping the whole row in them (rather than only the
  // chips, as before) is layout-neutral.
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={sortIds} strategy={horizontalListSortingStrategy}>
        <div
          // PP-06 — one row, 34–36px. `min-h-9` (36px) is the intended row
          // height, but `py-1` let the 30px sort chip drive the bar to 39px,
          // i.e. the acceptance criterion was never actually met (the original
          // e2e window of 34–40px hid it). `py-0.5` puts the content-driven
          // height at 35px so `min-h-9` governs and the bar lands on 36px.
          className="flex flex-wrap items-center gap-1 px-2 py-0.5 min-h-9 border-b border-border bg-background select-none"
          data-testid="sort-wizard-bar"
        >
          <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
            <ArrowUpDown className="h-3.5 w-3.5" />
            <span>Sort</span>
            {sorting.length > 0 && (
              // `mr-1` on top of the row's own `gap-1` puts 8px between the
              // count and the first sort chip — matching the `px-2` scale
              // used by the chip itself and the "then by..." button in this
              // same bar, rather than a one-off value. Without it, the count
              // sits flush against the chip's border (only the row's 4px
              // flex gap separated them).
              <span className="text-foreground font-medium mr-1">({sorting.length})</span>
            )}
          </div>

          {sorting.map((sort, index) => (
            <div key={`${sort.field}-${index}`} className="flex items-center gap-1">
              {index > 0 && (
                <span className="text-xs text-muted-foreground font-medium px-1">
                  then
                </span>
              )}
              <SortChip
                sort={sort}
                index={index}
                id={`sort-${sort.field}`}
                onToggleDirection={() => handleToggleDirection(index)}
                onRemove={() => handleRemoveSort(index)}
              />
            </div>
          ))}

          {/* Add sort button with popover */}
          <Popover open={isAddingSort} onOpenChange={setIsAddingSort}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs shrink-0"
                disabled={selectableColumns.length === 0}
              >
                <Plus className="h-3 w-3 mr-1" />
                {sorting.length > 0 ? 'then by...' : 'Add Sort'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-2" align="start">
              <div className="flex flex-col gap-2">
                <div className="text-xs font-medium text-muted-foreground">
                  Select a column to sort by
                </div>
                {/* Search input */}
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    value={columnSearch}
                    onChange={(e) => setColumnSearch(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    placeholder="Search columns..."
                    className="h-7 pl-7 text-xs"
                    autoFocus
                  />
                </div>
                {/* Column list */}
                <div className="max-h-48 overflow-y-auto" ref={listRef}>
                  {filteredColumns.length === 0 ? (
                    <div className="text-xs text-muted-foreground py-2 text-center">
                      No columns found
                    </div>
                  ) : (
                    (() => {
                      let selectableIndex = 0;
                      return filteredColumns.map((col) => {
                        const hasSort = columnHasSort(col.name);
                        const currentSelectableIndex = hasSort ? -1 : selectableIndex++;
                        const isSelected = !hasSort && currentSelectableIndex === selectedColumnIndex;
                        return (
                          <button
                            key={col.name}
                            data-index={currentSelectableIndex}
                            onClick={() => handleAddSort(col)}
                            onMouseEnter={() => {
                              if (!hasSort) setSelectedColumnIndex(currentSelectableIndex);
                            }}
                            disabled={hasSort}
                            className={`w-full flex items-center justify-between px-2 py-1.5 text-xs rounded ${
                              hasSort
                                ? 'opacity-50 cursor-not-allowed'
                                : isSelected
                                  ? 'bg-accent'
                                  : 'hover:bg-accent cursor-pointer'
                            }`}
                          >
                            <span className="font-medium">{col.name}</span>
                            <span className="text-muted-foreground text-[10px]">
                              {col.dataType}
                            </span>
                          </button>
                        );
                      });
                    })()
                  )}
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {/* Clear all button */}
          {sorting.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearAll}
              className="h-6 px-2 text-xs text-muted-foreground hover:text-destructive shrink-0"
            >
              <X className="h-3 w-3 mr-1" />
              Clear All
            </Button>
          )}
        </div>
      </SortableContext>
    </DndContext>
  );
}
